"use client"

import type { ReactNode } from "react"

export const inputClass =
  "w-full min-w-0 rounded-lg border border-white/10 bg-black/20 px-2.5 py-2 text-xs text-white outline-none transition placeholder:text-white/20 focus:border-white/25 focus:bg-black/30"

export function Field({
  label,
  value,
  onChange,
  placeholder,
  className = "",
}: {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
}) {
  return (
    <label className={`block min-w-0 ${className}`}>
      {label && (
        <span className="mb-1 block text-[9px] uppercase tracking-[.14em] text-white/30">
          {label}
        </span>
      )}

      <input
        value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={inputClass}
      />
    </label>
  )
}

export function Section({
  icon,
  title,
  description,
  action,
  children,
  collapsible = false,
  open = true,
  onToggle,
}: {
  icon: ReactNode
  title: string
  description?: string
  action?: ReactNode
  children: ReactNode
  collapsible?: boolean
  open?: boolean
  onToggle?: () => void
}) {
  const header = (
    <>
      <SectionIcon icon={icon} />

      <SectionText
        title={title}
        description={description}
      />

      {collapsible && (
        <Chevron
          open={open}
        />
      )}
    </>
  )

  return (
    <section className="overflow-hidden rounded-xl border border-white/10 bg-white/[.025]">
      <div className="flex min-h-11 items-center gap-2 border-b border-white/10 px-2.5">
        {collapsible ? (
          <button
            type="button"
            onClick={onToggle}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            {header}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {header}
          </div>
        )}

        {action}
      </div>

      {open && children}
    </section>
  )
}

function SectionIcon({
  icon,
}: {
  icon: ReactNode
}) {
  return (
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[.035]">
      {icon}
    </div>
  )
}

function SectionText({
  title,
  description,
}: {
  title: string
  description?: string
}) {
  return (
    <div className="min-w-0">
      <h2 className="truncate text-xs font-medium text-white/85">
        {title}
      </h2>

      {description && (
        <p className="truncate text-[9px] text-white/25">
          {description}
        </p>
      )}
    </div>
  )
}

function Chevron({
  open,
}: {
  open: boolean
}) {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={`ml-auto shrink-0 text-white/25 transition-transform ${
        open ? "rotate-180" : ""
      }`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

export function Stat({
  label,
  value,
}: {
  label: string
  value: string
}) {
  return (
    <div className="min-w-0 rounded-lg border border-white/10 bg-black/20 px-2 py-1.5">
      <p className="truncate text-[7px] uppercase tracking-[.12em] text-white/20">
        {label}
      </p>

      <p className="mt-0.5 truncate font-mono text-[10px] text-white/60">
        {value}
      </p>
    </div>
  )
}